<template>
    <canvas :id="'chart-'+id" class="bar-chart" :class="'bars-' + labels.length"></canvas>
</template>

<script>
import { defineComponent } from 'vue';
import moment from 'moment';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chartTooltip from '../../../../mixins/chart_tooltip';

export default defineComponent({
    name: 'HorizontalBarChart',
    mixins: [chartTooltip],
    props: {
        data: {
            required: true
        },
        labels: {
            type: Array,
            required: true
        },
        type: {
            type: String,
            default: 'number'
            //Can also be revenue or percent
        },
        id: {
            type: [String, Number],
            default: () => Math.floor(Math.random() * (10000))
        }
    },
    data() {
        return {
            chart: null,
            chart_selected: null,
            format_type: 'number',
            color: 'yellow',
            num_of_datasets: 0,
            if_chart_has_data: false,
            if_refreshing: false,
            applied_chart_filter: null
        };
    },
    watch: {
        chart_selected: {
            handler(newVal) {
                if (newVal?.data) {
                    this.$nextTick(() => {
                        this.format_type = newVal?.metric_formats?.[0] || 'number';
                        this.color = newVal?.metrics?.[0]?.color || 'yellow';
                        this.num_of_datasets = Object.keys(newVal.data).length;
                        this.renderChart();
                        this.if_chart_has_data = true;
                    });
                }
            },
            deep: true
        },
        if_refreshing: {
            handler() {
                this.$nextTick(() => {
                    this.renderChart();
                });
            }
        },
        applied_chart_filter: {
            handler(newVal) {
                this.if_chart_filter_applied = newVal?.if_chart_filter_applied && this.id === newVal?.section_chart_id;
                this.$nextTick(() => {
                    this.renderChart();
                });
            },
            deep: true
        }
    },
    mounted() {
        if (this.chart_selected?.data) {
            this.$nextTick(() => {
                this.format_type = this.chart_selected?.metric_formats?.[0] || 'number';
                this.color = this.chart_selected?.metrics?.[0]?.color || 'yellow';
                this.num_of_datasets = Object.keys(this.chart_selected.data).length;
                this.renderChart();
                this.if_chart_has_data = true;
            });
        }
    },
    methods: {
        renderChart() {
            if (!this.chart_selected?.data) return;

            const chartData = this.getChartData();
            const chartOptions = this.getChartOptions(chartData);

            // First destroy any existing chart
            if (this.chart) {
                this.chart.destroy();
                this.chart = null;
            }

            // Wait for the next tick to ensure DOM is ready and previous chart is fully destroyed
            this.$nextTick(() => {
                const chartEl = document.getElementById('chart-' + this.id);
                if (chartEl) {
                    try {
                        this.createChartWhenVisible(chartEl, chartOptions, (chartInstance) => {
                            this.chart = chartInstance;
                        });
                    } catch (error) {
                        console.error('Error creating chart:', error);
                    }
                }
            });
        },
        getLabels() {
            if(this.data instanceof Array)
                return null;

            //Get the keys of the data object
            return Object.keys(this.data);
        },
        getColor(source) {
            //Blue
            if(source == 'Facebook')
                return "52, 144, 220";
            //Purple
            if(source=="Email Prospecting")
                return "153, 102, 255";
            //Red
            if(source=="Other")
                return "224, 102, 102";
            //Green
            if(source=="Pay Per Click")
                return "147, 196, 125";
            //Gray
            if(source=="Google Grant")
                return "199, 204, 219";
            //Gray
            if(source=="QuickBooks")
                return "199, 204, 219";

            //Yellow
            if(this.type == 'number')
                return "245, 198, 53";
            //Green
            if(this.type == 'revenue')
                return "147, 196, 125";
            //Blue
            if(this.type == 'percent')
                return "52, 144, 220";

            //Yellow
            return "245, 198, 53";
        },
        getChartData() {
            const labels = this.chart_selected?.labels || [];
            const datasets = [];

            Object.keys(this.chart_selected?.data || {}).forEach((key, index) => {
                const metric = this.chart_selected?.metrics?.find(m => m.text === key);
                const color = metric?.color || this.color;
                const data = this.chart_selected.data[key];

                datasets.push({
                    label: key,
                    data: data,
                    backgroundColor: color,
                    borderColor: color,
                    borderWidth: 1
                });
            });

            return {
                labels: labels,
                datasets: datasets
            };
        },
        getChartOptions(chartData) {
            return {
                type: 'bar',
                data: chartData,
                options: {
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'top',
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    const value = context.raw;
                                    return this.formatValue(value);
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            beginAtZero: true,
                            ticks: {
                                callback: (value) => this.formatValue(value)
                            }
                        }
                    }
                }
            };
        },
        formatValue(value) {
            if (this.format_type === 'money') {
                return this.currency_with_zero(value);
            } else if (this.format_type === 'percent') {
                return this.percentage(value);
            } else if (this.format_type === 'number') {
                return this.number_with_decimal(value);
            } else if (this.format_type === 'date') {
                return this.date(value);
            }
            return value;
        }
    }
});
</script>
